import { alpha } from "@mui/material/styles";
import PTR from "pulltorefreshjs";
import { useEffect } from "react";
import { INK_BLACK, STATUS_BADGE } from "@/theme/tokens";

const radius = 10;

// brand fallback — does not re-tint with user theme (injected as plain HTML string outside React)
const SPINNER_COLOR = STATUS_BADGE.BRAND_FALLBACK;

const spinnerSvg = `
<div style="
  display:flex;
  justify-content:center;
  align-items:center;
  width:100%;
  height:100%;
">
	<svg
	  viewBox="0 0 24 24"
	  width="24"
	  height="24"
	  role="progressbar"
	  aria-label="Loading"
	  style="display:block"
	>
	  <!-- track -->
	  <circle cx="12" cy="12" r="${radius}" fill="none" stroke="${SPINNER_COLOR}" opacity="0.2" stroke-width="3" />
	  <!-- arc -->
	  <path
		d="M12 2 a10 10 0 0 1 10 10"
		fill="none"
		stroke="${SPINNER_COLOR}"
		stroke-width="3"
		stroke-linecap="round"
	  >
		<animateTransform
		  attributeName="transform"
		  type="rotate"
		  from="0 12 12"
		  to="360 12 12"
		  dur="0.8s"
		  repeatCount="indefinite"
		/>
	  </path>
	</svg>
</div>
`;

const prefixClass = "msv--";
const ptrElement = ".msv-ptr";

const styleOverride = `
	.__PREFIX__ptr {
	  pointer-events: none;
	  font-size: 0.85em;
	  font-weight: bold;
	  top: 0;
	  height: 0;
	  transition: height 0.3s, min-height 0.3s;
	  text-align: center;
	  width: 100%;
	  overflow: hidden;
	  display: flex;
	  align-items: flex-end;
	  align-content: stretch;
	}
	
	.__PREFIX__box {
	  padding: 10px;
	  flex-basis: 100%;
	}
	
	.__PREFIX__pull {
	  transition: none;
	}
	
	.__PREFIX__text {
	  margin-top: 0px;
	  color: ${alpha(INK_BLACK, 0)};
	}
	
	.__PREFIX__icon {
	  color: ${alpha(INK_BLACK, 0.3)};
	  transition: transform .3s;
	}
	
	
	/*
	When at the top of the page, disable vertical overscroll so passive touch
	listeners can take over.
	*/
	.__PREFIX__top {
	  touch-action: pan-x pan-down pinch-zoom;
	}
	
	.__PREFIX__release .__PREFIX__icon {
	  transform: rotate(180deg);
	}
`;

type Props = {
	/**
	 * Element should have an ID!!!*/
	scrollerEl: HTMLElement | null;
	/**
	 * Element should have an ID!!!*/
	childrenContainerEl: HTMLElement | null;
	onRefresh: () => Promise<void>;
	distance?: number;
	shouldPullToRefresh?: () => boolean;
};

/**
 * Pull-to-refresh component using pulltorefreshjs.
 * Requires the scroll container and the children container elements to have IDs.
 * These elements should also be stable across renders, provide keys if necessary.
 *
 * Usage example:
 * @example
 * ```
 * <Box
 * 		ref={scrollRef}
 * 		id={`scrollable-bedside-home-container"}
 * 		key={"scrollable-bedside-home-container"}
 * 		display={"flex"}
 * 		flexDirection={"column"}
 * 		width={"100%"}
 * 		height={`calc(100% - ${staffingHeaderHeight}px)`}
 * 		position={"relative"}
 * 		p={1}
 * 		py={0}
 * 		style={{
 * 			overflowY: "auto",
 * 			overscrollBehaviorY: "contain",
 * 			WebkitOverflowScrolling: "touch",
 * 			background: "#f8f8f8",
 * 		}}
 * 	>
 * 				<PullToRefresh
 * 				childrenContainerEl={childrenContainerRef.current}
 * 				scrollerEl={scrollRef.current}
 * 				onRefresh={handleBedsideRefresh} />
 * 				<Box key={"bedside-home-content-box"}
 * 				ref={childrenContainerRef}
 * 				id={"bedside-home-content-box"}>
 *
 *	 				 <ShiftWeekList
 							shifts={getShifts(transformedShifts)}
 *	 						selectedDate={selectedDate.toFormat("yyyy-MM-dd")}
 *	 						onDateSelect={handleDateSelect}
 *	 						expandedShiftIds={expandedShiftIds}
 *	 						onExpandOpenShift={handleExpandClick}
 *	 						allOpenShifts={allOpenShifts}
 *	 						setAllOpenShifts={setAllOpenShifts}
 *	 					/>
 * 				</Box>
 * 	</Box>
 * 	```
 */
export function PullToRefresh({ scrollerEl, childrenContainerEl, onRefresh, distance = 60, shouldPullToRefresh }: Props) {
	useEffect(() => {
		if (!scrollerEl) return;
		if (!scrollerEl.id) {
			console.warn("PullToRefresh: scroll element must have an ID for pulltorefreshjs to work properly.");
			return;
		}
		const scrollerSelector = `#${scrollerEl.id}`;

		if (!childrenContainerEl) return;
		if (!childrenContainerEl.id) {
			console.warn("PullToRefresh: children container element must have an ID for pulltorefreshjs to work properly.");
			return;
		}
		const mainSelector = `#${childrenContainerEl.id}`;

		const api = PTR.init({
			mainElement: mainSelector,
			triggerElement: scrollerSelector,
			distThreshold: distance,
			distReload: distance,
			classPrefix: prefixClass,
			ptrElement: ptrElement,
			cssProp: "padding-top",
			iconRefreshing: spinnerSvg,
			getStyles: () => styleOverride,

			shouldPullToRefresh: shouldPullToRefresh ?? (() => scrollerEl.scrollTop <= 1),
			onRefresh: () => onRefresh(),
		});

		return () => {
			try {
				api.destroy();
				PTR.destroyAll?.();
			} catch {}
		};
	}, [scrollerEl, childrenContainerEl, onRefresh, distance, shouldPullToRefresh]);

	return null;
}
